/**
 * @file Registre des sections du panel admin (miroir frontend).
 * @module packages.common.config.adminSections
 *
 * Source unique de vérité côté frontend du système d'accès admin : pilote le
 * menu latéral, la nav mobile, les guards de routes (AdminSectionGuard) et
 * l'écran de gestion de l'équipe. Miroir du registre backend
 * (backend/src/config/adminSections.js) — les clés et scopes doivent rester
 * identiques des deux côtés.
 *
 * Ajouter une section au panel = une entrée ici + une entrée backend +
 * requireAdminSection('<clé>') sur le router.
 */

import {paths} from 'packages/common/routes/paths';

/** Scopes de données possibles. */
export const ADMIN_SCOPES = {
  ALL: 'all',
  PORTFOLIO: 'portfolio',
  OWN: 'own',
};

/** Libellés des scopes pour l'écran de gestion des accès. */
export const ADMIN_SCOPE_LABELS = {
  [ADMIN_SCOPES.ALL]: 'Toutes les données',
  [ADMIN_SCOPES.PORTFOLIO]: 'Son portefeuille',
  [ADMIN_SCOPES.OWN]: 'Ses éléments',
};

/**
 * Modes d'accès à une section (miroir backend : appliqué par méthode HTTP).
 */
export const ADMIN_MODES = {
  READ: 'read',
  MANAGE: 'manage',
};

/** Libellés des modes pour l'écran de gestion des accès. */
export const ADMIN_MODE_LABELS = {
  [ADMIN_MODES.READ]: 'Lecture',
  [ADMIN_MODES.MANAGE]: 'Gestion',
};

/** Groupes d'affichage du menu latéral, dans l'ordre. */
export const ADMIN_NAV_GROUPS = [
  {key: 'merchants', subheader: 'Établissements'},
  {key: 'gestion', subheader: 'Gestion'},
];

/**
 * Sections attribuables à un collaborateur.
 *
 * `navItems` décrit les entrées de menu qu'une section débloque (une section
 * peut porter plusieurs entrées, ex. companies → Établissements + Nouveau).
 * `scopes` liste les scopes proposables dans l'écran de gestion d'équipe.
 */
export const ADMIN_SECTIONS = [
  {
    key: 'accounts',
    label: 'Comptes clients',
    group: 'merchants',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Comptes', path: paths.admin.merchants.accounts, icon: 'clarity:users-line'}],
  },
  {
    key: 'companies',
    label: 'Établissements',
    group: 'merchants',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [
      {title: 'Établissements', path: paths.admin.merchants.companies, icon: 'clarity:building-line'},
      {title: 'Nouveau', path: paths.admin.merchants.createCompany, icon: 'clarity:plus-circle-line', mode: ADMIN_MODES.MANAGE},
    ],
  },
  {
    key: 'subscriptions',
    label: 'Abonnements',
    group: 'merchants',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Abonnements', path: paths.admin.merchants.subscriptions, icon: 'clarity:credit-card-line'}],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    group: 'gestion',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Analytics', path: paths.admin.analytics, icon: 'clarity:analytics-line'}],
  },
  {
    key: 'invoices',
    label: 'Factures',
    group: 'gestion',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Factures', path: paths.admin.finances.invoices, icon: 'clarity:wallet-line'}],
  },
  {
    key: 'emails',
    label: 'Emails',
    group: 'gestion',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Emails', path: paths.admin.communications.emails, icon: 'clarity:email-line'}],
  },
  {
    key: 'crm',
    label: 'CRM',
    group: 'gestion',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.OWN],
    navItems: [{title: 'CRM', path: paths.admin.crm, icon: 'clarity:employee-group-line'}],
  },
  {
    key: 'commissions',
    label: 'Commissions',
    group: 'gestion',
    scopes: [ADMIN_SCOPES.ALL, ADMIN_SCOPES.PORTFOLIO],
    navItems: [{title: 'Commissions', path: paths.admin.finances.commissions, icon: 'mdi:percent-outline'}],
  },
];

/**
 * Templates de permissions par profil métier, pré-remplis à la création d'un
 * collaborateur (modifiables avant enregistrement).
 */
export const ADMIN_PROFILE_TEMPLATES = {
  commercial: [{key: 'crm', scope: ADMIN_SCOPES.OWN, mode: ADMIN_MODES.READ}],
  partner: [
    {key: 'companies', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
    {key: 'subscriptions', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
    {key: 'invoices', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
    {key: 'analytics', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
    {key: 'emails', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
    {key: 'commissions', scope: ADMIN_SCOPES.PORTFOLIO, mode: ADMIN_MODES.READ},
  ],
  custom: [],
};

/**
 * Retrouve une section du registre par sa clé.
 *
 * @param {string} key - Clé de section
 * @return {Object|undefined} Entrée du registre
 */
export const getAdminSection = (key) => ADMIN_SECTIONS.find((section) => section.key === key);
